import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, TicketIcon } from "@heroicons/react/20/solid";
import {
    ShieldCheckIcon,
    ClockIcon,
    DocumentTextIcon,
    ServerStackIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    LinkIcon,
    PlayIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ArrowDownTrayIcon,
    EyeIcon,
    LockClosedIcon,
    NoSymbolIcon,
    ArrowPathIcon,
    MagnifyingGlassIcon,
    CpuChipIcon,
    GlobeAltIcon,
    BoltIcon,
} from "@heroicons/react/20/solid";
import { Heading } from "src/components/base/heading";
import { Badge } from "src/components/base/badge";
import { Button } from "src/components/base/button";
import { Switch } from "src/components/base/switch";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import { Checkbox } from "src/components/base/checkbox";
import type { View, ViewContext } from "../types";
import { VIEW_LABELS, VIEW_COLORS } from "../types";
import { UNIFIED_EVENTS } from "../data/events";
import { Card, StatCard } from "../shared";
import { LocalTab, TabMenu } from "src/components/base/tab-menu";

// ---------------------------------------------------------------------------
// Types & mock data
// ---------------------------------------------------------------------------

type TabId = "details" | "timeline" | "evidence" | "containment";

const severityColor = { critical: "red", high: "orange", medium: "yellow", low: "zinc" } as const;
const statusMap: Record<string, string> = {
    "SOC-1247": "Open",
    "SOC-1248": "Investigating",
    "SOC-1249": "Investigating",
    "SOC-1250": "Open",
    "SOC-1251": "Resolved",
    "SOC-1252": "Investigating",
};
const sourceMap: Record<string, string> = {
    "SOC-1247": "fw-prod-01",
    "SOC-1248": "edr-ws-142",
    "SOC-1249": "proxy-ext-02",
    "SOC-1250": "ad-dc-01",
    "SOC-1251": "ids-dmz-03",
    "SOC-1252": "edr-srv-08",
};
const mitreMap: Record<string, string> = {
    "SOC-1247": "TA0006 Credential Access",
    "SOC-1248": "TA0002 Execution",
    "SOC-1249": "TA0010 Exfiltration",
    "SOC-1250": "TA0001 Initial Access",
    "SOC-1251": "TA0007 Discovery",
    "SOC-1252": "TA0002 Execution",
};

interface TimelineEvent {
    id: string;
    time: string;
    title: string;
    description: string;
    system: string;
    severity: "critical" | "high" | "medium" | "low" | "info";
    type: "detection" | "correlation" | "action" | "escalation";
}

interface EvidenceItem {
    id: string;
    name: string;
    type: "network_capture" | "memory_dump" | "log_excerpt" | "screenshot" | "malware_sample";
    size: string;
    collectedAt: string;
    collectedBy: string;
    hash: string;
    status: "collected" | "analyzing" | "analyzed";
}

interface ContainmentAction {
    id: string;
    action: string;
    description: string;
    status: "pending" | "in_progress" | "completed" | "skipped";
    assignee: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    completedAt: string | null;
    critical: boolean;
}

interface AffectedAsset {
    hostname: string;
    ip: string;
    type: string;
    status: "compromised" | "at_risk" | "clean" | "isolated";
    lastScan: string;
    compliance: string;
}

interface PlaybookStep {
    id: string;
    phase: string;
    title: string;
    description: string;
    completed: boolean;
    substeps: { text: string; completed: boolean }[];
}

interface RelatedTicket {
    id: string;
    title: string;
    status: string;
    assignee: string;
    priority: string;
}

// Risk score dimensions
interface RiskDimension {
    label: string;
    value: number;
    max: number;
}

// ---------------------------------------------------------------------------
// Mock data factories
// ---------------------------------------------------------------------------

function getTimelineEvents(eventId: string): TimelineEvent[] {
    return [
        {
            id: "tl-1",
            time: "14:20:01",
            title: "Initial Detection",
            description: "Firewall rule triggered on anomalous traffic pattern from 185.143.223.1",
            system: "soc",
            severity: "high",
            type: "detection",
        },
        {
            id: "tl-2",
            time: "14:20:15",
            title: "NDR Correlation",
            description: "Network Detection correlated C2 callback attempt on TCP/443",
            system: "ndr",
            severity: "high",
            type: "correlation",
        },
        {
            id: "tl-3",
            time: "14:21:02",
            title: "ZTNA Session Check",
            description: "ZTNA verified no active sessions from source IP, trust score evaluated",
            system: "ztna",
            severity: "medium",
            type: "correlation",
        },
        {
            id: "tl-4",
            time: "14:21:30",
            title: "Automated Block",
            description: "Source IP 185.143.223.1 automatically blocked at perimeter firewall",
            system: "soc",
            severity: "info",
            type: "action",
        },
        {
            id: "tl-5",
            time: "14:22:00",
            title: "Alert Escalated",
            description: "Alert escalated to SOC Tier 2 due to APT-Sigma-7 TTP match",
            system: "soc",
            severity: "critical",
            type: "escalation",
        },
        {
            id: "tl-6",
            time: "14:22:45",
            title: "Lateral Movement Blocked",
            description: "NDR blocked SMB lateral movement attempt to 10.0.8.102",
            system: "ndr",
            severity: "high",
            type: "action",
        },
        {
            id: "tl-7",
            time: "14:23:00",
            title: "Alert Created",
            description: `SOC Alert ${eventId} created with critical severity`,
            system: "soc",
            severity: "critical",
            type: "detection",
        },
        {
            id: "tl-8",
            time: "14:23:15",
            title: "Notification Sent",
            description: "Critical alert notification sent to SOC team and Slack",
            system: "soc",
            severity: "info",
            type: "action",
        },
        {
            id: "tl-9",
            time: "14:25:00",
            title: "Investigation Started",
            description: "SOC analyst m.mustermann began investigation",
            system: "soc",
            severity: "info",
            type: "action",
        },
        {
            id: "tl-10",
            time: "14:28:00",
            title: "Memory Dump Initiated",
            description: "Remote memory acquisition started on fw-prod-01",
            system: "soc",
            severity: "info",
            type: "action",
        },
    ];
}

const MOCK_EVIDENCE: EvidenceItem[] = [
    {
        id: "ev-1",
        name: "fw-prod-01_pcap_20260313.pcapng",
        type: "network_capture",
        size: "142 MB",
        collectedAt: "14:24:00",
        collectedBy: "Auto-Collector",
        hash: "sha256:a1b2c3d4e5...f6g7h8i9",
        status: "analyzed",
    },
    {
        id: "ev-2",
        name: "fw-prod-01_memdump.raw",
        type: "memory_dump",
        size: "4.2 GB",
        collectedAt: "14:28:00",
        collectedBy: "m.mustermann",
        hash: "sha256:j0k1l2m3...n4o5p6q7",
        status: "analyzing",
    },
    {
        id: "ev-3",
        name: "fw-prod-01_auth.log",
        type: "log_excerpt",
        size: "2.3 MB",
        collectedAt: "14:25:00",
        collectedBy: "Auto-Collector",
        hash: "sha256:r8s9t0u1...v2w3x4y5",
        status: "analyzed",
    },
    {
        id: "ev-4",
        name: "ndr_flow_185.143.223.1.json",
        type: "log_excerpt",
        size: "856 KB",
        collectedAt: "14:26:00",
        collectedBy: "Auto-Collector",
        hash: "sha256:z6a7b8c9...d0e1f2g3",
        status: "analyzed",
    },
    {
        id: "ev-5",
        name: "payload_sample.bin",
        type: "malware_sample",
        size: "48 KB",
        collectedAt: "14:30:00",
        collectedBy: "s.braun",
        hash: "sha256:h4i5j6k7...l8m9n0o1",
        status: "collected",
    },
];

function getContainmentActions(): ContainmentAction[] {
    return [
        {
            id: "ca-1",
            action: "Isolate Host",
            description: "Network-isolate fw-prod-01 from production segment",
            status: "completed",
            assignee: "m.mustermann",
            icon: LockClosedIcon,
            completedAt: "14:25:00",
            critical: true,
        },
        {
            id: "ca-2",
            action: "Block Source IP",
            description: "Block 185.143.223.1 at all perimeter firewalls and proxy",
            status: "completed",
            assignee: "Auto-Response",
            icon: NoSymbolIcon,
            completedAt: "14:21:30",
            critical: true,
        },
        {
            id: "ca-3",
            action: "Revoke Sessions",
            description: "Revoke all active sessions associated with compromised credentials",
            status: "in_progress",
            assignee: "a.weber",
            icon: ArrowPathIcon,
            completedAt: null,
            critical: true,
        },
        {
            id: "ca-4",
            action: "Scan Endpoints",
            description: "Run full endpoint scan on all assets in the blast radius",
            status: "pending",
            assignee: "j.mueller",
            icon: MagnifyingGlassIcon,
            completedAt: null,
            critical: false,
        },
        {
            id: "ca-5",
            action: "DNS Sinkhole",
            description: "Sinkhole all C2 domains associated with APT-Sigma-7",
            status: "completed",
            assignee: "Auto-Response",
            icon: GlobeAltIcon,
            completedAt: "14:22:00",
            critical: true,
        },
        {
            id: "ca-6",
            action: "Credential Reset",
            description: "Force password reset for all accounts accessed from compromised host",
            status: "pending",
            assignee: "a.weber",
            icon: ShieldCheckIcon,
            completedAt: null,
            critical: false,
        },
    ];
}

const MOCK_AFFECTED_ASSETS: AffectedAsset[] = [
    {
        hostname: "fw-prod-01",
        ip: "10.0.1.1",
        type: "Firewall",
        status: "isolated",
        lastScan: "14:25:00",
        compliance: "Non-compliant",
    },
    {
        hostname: "edr-ws-142",
        ip: "10.0.8.102",
        type: "Workstation",
        status: "at_risk",
        lastScan: "14:22:00",
        compliance: "Partial",
    },
    {
        hostname: "ad-dc-01",
        ip: "10.0.0.10",
        type: "Domain Controller",
        status: "at_risk",
        lastScan: "14:20:00",
        compliance: "Compliant",
    },
    {
        hostname: "proxy-ext-02",
        ip: "10.0.2.5",
        type: "Proxy",
        status: "clean",
        lastScan: "14:23:00",
        compliance: "Compliant",
    },
    {
        hostname: "switch-core-01",
        ip: "10.0.0.1",
        type: "Switch",
        status: "clean",
        lastScan: "14:18:00",
        compliance: "Compliant",
    },
];

function getPlaybookSteps(): PlaybookStep[] {
    return [
        {
            id: "pb-1",
            phase: "Identification",
            title: "Confirm and classify the incident",
            description: "Validate alert, determine scope, and classify severity",
            completed: true,
            substeps: [
                { text: "Verify alert is not a false positive", completed: true },
                { text: "Determine affected systems and data", completed: true },
                { text: "Classify incident severity (Critical)", completed: true },
                { text: "Assign incident commander", completed: true },
            ],
        },
        {
            id: "pb-2",
            phase: "Containment",
            title: "Limit the blast radius",
            description: "Isolate affected systems and prevent further compromise",
            completed: false,
            substeps: [
                { text: "Isolate compromised hosts from network", completed: true },
                { text: "Block attacker IPs at perimeter", completed: true },
                { text: "Revoke compromised credentials", completed: false },
                { text: "Preserve forensic evidence", completed: true },
            ],
        },
        {
            id: "pb-3",
            phase: "Eradication",
            title: "Remove the threat",
            description: "Eliminate malware, backdoors, and attacker access",
            completed: false,
            substeps: [
                { text: "Remove malware from affected systems", completed: false },
                { text: "Patch exploited vulnerabilities", completed: false },
                { text: "Reset all potentially compromised accounts", completed: false },
            ],
        },
        {
            id: "pb-4",
            phase: "Recovery",
            title: "Restore normal operations",
            description: "Bring systems back online and verify integrity",
            completed: false,
            substeps: [
                { text: "Restore from known-good backups", completed: false },
                { text: "Verify system integrity", completed: false },
                { text: "Monitor for re-compromise indicators", completed: false },
                { text: "Gradually restore network access", completed: false },
            ],
        },
        {
            id: "pb-5",
            phase: "Lessons Learned",
            title: "Post-incident review",
            description: "Document findings and improve defenses",
            completed: false,
            substeps: [
                { text: "Document full incident timeline", completed: false },
                { text: "Update detection rules", completed: false },
                { text: "Conduct post-mortem meeting", completed: false },
            ],
        },
    ];
}

const MOCK_RELATED_TICKETS: RelatedTicket[] = [
    {
        id: "TKT-2041",
        title: "Investigate APT-Sigma-7 campaign",
        status: "In Progress",
        assignee: "m.mustermann",
        priority: "Critical",
    },
    {
        id: "TKT-2038",
        title: "Block IOCs from threat intel feed",
        status: "Completed",
        assignee: "s.braun",
        priority: "High",
    },
    {
        id: "TKT-2035",
        title: "Firewall rule review - fw-prod-01",
        status: "Open",
        assignee: "j.mueller",
        priority: "Medium",
    },
];

const RISK_DIMENSIONS: RiskDimension[] = [
    { label: "Severity", value: 95, max: 100 },
    { label: "Blast Radius", value: 72, max: 100 },
    { label: "Data Impact", value: 60, max: 100 },
    { label: "Persistence", value: 85, max: 100 },
    { label: "Lateral Movement", value: 78, max: 100 },
    { label: "Evasion", value: 55, max: 100 },
];

const assetStatusColor: Record<string, "red" | "orange" | "green" | "blue"> = {
    compromised: "red",
    at_risk: "orange",
    clean: "green",
    isolated: "blue",
};

const containmentStatusColor: Record<string, string> = {
    pending: "text-zinc-400",
    in_progress: "text-yellow-500",
    completed: "text-green-500",
    skipped: "text-zinc-300",
};

const evidenceTypeIcon: Record<string, string> = {
    network_capture: "PCAP",
    memory_dump: "MEM",
    log_excerpt: "LOG",
    screenshot: "IMG",
    malware_sample: "MAL",
};

const evidenceTypeColor: Record<string, "blue" | "purple" | "green" | "orange" | "red"> = {
    network_capture: "blue",
    memory_dump: "purple",
    log_excerpt: "green",
    screenshot: "orange",
    malware_sample: "red",
};

const timelineTypeColor: Record<string, string> = {
    detection: "bg-red-500",
    correlation: "bg-blue-500",
    action: "bg-green-500",
    escalation: "bg-orange-500",
};

const timelineSevColor: Record<string, string> = {
    critical: "border-red-400 shadow-red-400/30",
    high: "border-orange-400 shadow-orange-400/30",
    medium: "border-yellow-400 shadow-yellow-400/30",
    low: "border-zinc-400 shadow-zinc-400/30",
    info: "border-blue-400 shadow-blue-400/30",
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RiskGauge({ score, label }: { score: number; label: string }) {
    const circumference = 2 * Math.PI * 45;
    const dashOffset = circumference - (score / 100) * circumference;
    const color = score >= 80 ? "#ef4444" : score >= 60 ? "#f97316" : score >= 40 ? "#eab308" : "#22c55e";

    return (
        <div className="flex flex-col items-center gap-2">
            <svg width="120" height="120" viewBox="0 0 120 120" className="drop-shadow-lg">
                <circle
                    cx="60"
                    cy="60"
                    r="45"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="8"
                    className="text-zinc-200 dark:text-zinc-700"
                />
                <circle
                    cx="60"
                    cy="60"
                    r="45"
                    fill="none"
                    stroke={color}
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    transform="rotate(-90 60 60)"
                    className="transition-all duration-1000 ease-out"
                />
                <text
                    x="60"
                    y="55"
                    textAnchor="middle"
                    className="fill-zinc-900 text-2xl font-extrabold dark:fill-white"
                    fontSize="28"
                >
                    {score}
                </text>
                <text x="60" y="75" textAnchor="middle" className="fill-zinc-500 text-xs" fontSize="11">
                    {label}
                </text>
            </svg>
        </div>
    );
}

function RadarChart({ dimensions }: { dimensions: RiskDimension[] }) {
    const size = 200;
    const center = size / 2;
    const radius = 70;
    const angleStep = (2 * Math.PI) / dimensions.length;

    const getPoint = (index: number, value: number, max: number) => {
        const angle = index * angleStep - Math.PI / 2;
        const r = (value / max) * radius;
        return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) };
    };

    const dataPoints = dimensions.map((d, i) => getPoint(i, d.value, d.max));
    const dataPath = dataPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ") + " Z";

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
            {/* Grid circles */}
            {[0.25, 0.5, 0.75, 1].map((scale) => (
                <polygon
                    key={scale}
                    points={dimensions
                        .map((_, i) => {
                            const p = getPoint(i, scale * 100, 100);
                            return `${p.x},${p.y}`;
                        })
                        .join(" ")}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="0.5"
                    className="text-zinc-200 dark:text-zinc-700"
                />
            ))}
            {/* Axes */}
            {dimensions.map((d, i) => {
                const p = getPoint(i, 100, 100);
                const labelP = getPoint(i, 120, 100);
                return (
                    <g key={d.label}>
                        <line
                            x1={center}
                            y1={center}
                            x2={p.x}
                            y2={p.y}
                            stroke="currentColor"
                            strokeWidth="0.5"
                            className="text-zinc-300 dark:text-zinc-600"
                        />
                        <text
                            x={labelP.x}
                            y={labelP.y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className="fill-zinc-500 dark:fill-zinc-400"
                            fontSize="8"
                        >
                            {d.label}
                        </text>
                    </g>
                );
            })}
            {/* Data polygon */}
            <path d={dataPath} fill="rgba(59, 130, 246, 0.15)" stroke="#3b82f6" strokeWidth="2" />
            {/* Data points */}
            {dataPoints.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="3" fill="#3b82f6" />
            ))}
        </svg>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SocAlertDetailView({
    eventId,
    onNavigate,
    onNavigateDetail,
}: {
    eventId: string;
    onNavigate: (v: View) => void;
    onNavigateDetail: (ctx: ViewContext) => void;
}) {
    const { t } = useTranslation();
    const event = UNIFIED_EVENTS.find((e) => e.id === eventId);

    const [activeTab, setActiveTab] = useState<TabId>("details");
    const [containmentActions, setContainmentActions] = useState<ContainmentAction[]>(getContainmentActions);
    const [playbook, setPlaybook] = useState<PlaybookStep[]>(getPlaybookSteps);
    const [expandedPlaybookStep, setExpandedPlaybookStep] = useState<string | null>("pb-2");

    if (!event) {
        return (
            <div>
                <Button plain onClick={() => onNavigate("soc")}>
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t("socDetail.back")}
                </Button>
                <p className="mt-8 text-zinc-500">{t("socDetail.notFound")}</p>
            </div>
        );
    }

    const correlatedEvents = UNIFIED_EVENTS.filter(
        (e) =>
            e.id !== event.id &&
            (event.correlated.includes(e.system) ||
                e.correlated.includes(event.system) ||
                (event.actor && e.actor === event.actor)),
    );

    const timelineEvents = getTimelineEvents(eventId);
    const overallRisk = Math.round(RISK_DIMENSIONS.reduce((sum, d) => sum + d.value, 0) / RISK_DIMENSIONS.length);

    const toggleContainment = (id: string) => {
        setContainmentActions((prev) =>
            prev.map((ca) =>
                ca.id === id
                    ? {
                          ...ca,
                          status: ca.status === "completed" ? "pending" : "completed",
                          completedAt:
                              ca.status === "completed"
                                  ? null
                                  : new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }),
                      }
                    : ca,
            ),
        );
    };

    const togglePlaybookSubstep = (stepId: string, substepIdx: number) => {
        setPlaybook((prev) =>
            prev.map((step) => {
                if (step.id !== stepId) return step;
                const newSubsteps = step.substeps.map((ss, i) =>
                    i === substepIdx ? { ...ss, completed: !ss.completed } : ss,
                );
                return { ...step, substeps: newSubsteps, completed: newSubsteps.every((ss) => ss.completed) };
            }),
        );
    };

    return (
        <>
            {/* Back button */}
            <Button plain onClick={() => onNavigate("soc")}>
                <ArrowLeftIcon className="h-4 w-4" />
                {t("socDetail.back")}
            </Button>

            {/* Header */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="font-mono text-sm text-zinc-400">{event.id}</span>
                <Heading>{event.title}</Heading>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
                <Badge color={severityColor[event.severity]}>{event.severity.toUpperCase()}</Badge>
                <Badge color="zinc">{statusMap[event.id] ?? "Open"}</Badge>
                {event.actor && <Badge color="red">{event.actor}</Badge>}
            </div>

            {/* Tabs */}
            <div className="mt-6">
                <TabMenu>
                    <LocalTab active={activeTab === "details"} onClick={() => setActiveTab("details")}>
                        Details
                    </LocalTab>
                    <LocalTab active={activeTab === "timeline"} onClick={() => setActiveTab("timeline")}>
                        Timeline
                    </LocalTab>
                    <LocalTab active={activeTab === "evidence"} onClick={() => setActiveTab("evidence")}>
                        Evidence
                    </LocalTab>
                    <LocalTab active={activeTab === "containment"} onClick={() => setActiveTab("containment")}>
                        Containment
                    </LocalTab>
                </TabMenu>
            </div>

            {/* ============================================================ */}
            {/* DETAILS TAB                                                  */}
            {/* ============================================================ */}
            {activeTab === "details" && (
                <div className="mt-6 space-y-6">
                    {/* Risk + Details row */}
                    <div className="grid gap-6 lg:grid-cols-3">
                        <Card className="lg:col-span-2">
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                {t("socDetail.details")}
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                                {event.details}
                            </p>
                            <dl className="mt-4 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <dt className="text-zinc-500">{t("socDetail.source")}</dt>
                                    <dd className="font-mono text-zinc-900 dark:text-white">
                                        {sourceMap[event.id] ?? "—"}
                                    </dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-zinc-500">{t("socDetail.time")}</dt>
                                    <dd className="font-mono text-zinc-900 dark:text-white">{event.time}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-zinc-500">{t("socDetail.mitre")}</dt>
                                    <dd className="text-zinc-900 dark:text-white">{mitreMap[event.id] ?? "—"}</dd>
                                </div>
                            </dl>
                        </Card>

                        {/* Risk Score */}
                        <Card>
                            <h3 className="mb-4 text-sm font-semibold text-zinc-900 dark:text-white">Risk Score</h3>
                            <div className="flex flex-col items-center gap-4">
                                <RiskGauge score={overallRisk} label="Overall Risk" />
                                <RadarChart dimensions={RISK_DIMENSIONS} />
                            </div>
                        </Card>
                    </div>

                    {/* Correlated Systems + Affected Assets */}
                    <div className="grid gap-6 lg:grid-cols-2">
                        <Card>
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                {t("socDetail.correlatedSystems")}
                            </h3>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {event.correlated.map((sys) => (
                                    <button key={sys} onClick={() => onNavigate(sys)}>
                                        <Badge color={VIEW_COLORS[sys]}>{VIEW_LABELS[sys]}</Badge>
                                    </button>
                                ))}
                            </div>
                        </Card>
                        <Card>
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                Affected Assets ({MOCK_AFFECTED_ASSETS.length})
                            </h3>
                            <Table dense className="mt-3">
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>Host</TableHeader>
                                        <TableHeader>IP</TableHeader>
                                        <TableHeader>Type</TableHeader>
                                        <TableHeader>Status</TableHeader>
                                        <TableHeader>Compliance</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {MOCK_AFFECTED_ASSETS.map((asset) => (
                                        <TableRow key={asset.hostname}>
                                            <TableCell className="font-mono text-xs font-medium">
                                                {asset.hostname}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">{asset.ip}</TableCell>
                                            <TableCell className="text-xs">{asset.type}</TableCell>
                                            <TableCell>
                                                <Badge color={assetStatusColor[asset.status]}>{asset.status}</Badge>
                                            </TableCell>
                                            <TableCell className="text-xs">{asset.compliance}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Card>
                    </div>

                    {/* Correlated Events */}
                    <Card>
                        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                            {t("socDetail.correlatedEvents")} ({correlatedEvents.length})
                        </h3>
                        <div className="mt-4 space-y-0">
                            {correlatedEvents.map((re) => (
                                <div
                                    key={re.id}
                                    className="relative border-l-2 border-blue-200/60 py-3 pl-5 dark:border-blue-800/60"
                                >
                                    <div className="absolute -left-[5px] top-4 h-2.5 w-2.5 rounded-full bg-blue-400 shadow-sm shadow-blue-400/50" />
                                    <div className="flex items-center gap-2">
                                        <Badge color={VIEW_COLORS[re.system]}>{VIEW_LABELS[re.system]}</Badge>
                                        <span className="font-mono text-xs text-zinc-400">{re.id}</span>
                                        <span className="font-mono text-xs text-zinc-400">{re.time}</span>
                                    </div>
                                    <p className="mt-1 text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                        {re.title}
                                    </p>
                                    <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">{re.details}</p>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Related Tickets */}
                    <Card>
                        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Related Tickets</h3>
                        <Table dense className="mt-3">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>ID</TableHeader>
                                    <TableHeader>Title</TableHeader>
                                    <TableHeader>Status</TableHeader>
                                    <TableHeader>Assignee</TableHeader>
                                    <TableHeader>Priority</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {MOCK_RELATED_TICKETS.map((ticket) => (
                                    <TableRow key={ticket.id}>
                                        <TableCell className="font-mono text-xs font-medium text-blue-600 dark:text-blue-400">
                                            {ticket.id}
                                        </TableCell>
                                        <TableCell className="text-sm">{ticket.title}</TableCell>
                                        <TableCell>
                                            <Badge
                                                color={
                                                    ticket.status === "Completed"
                                                        ? "green"
                                                        : ticket.status === "In Progress"
                                                          ? "orange"
                                                          : "zinc"
                                                }
                                            >
                                                {ticket.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-xs">{ticket.assignee}</TableCell>
                                        <TableCell>
                                            <Badge
                                                color={
                                                    ticket.priority === "Critical"
                                                        ? "red"
                                                        : ticket.priority === "High"
                                                          ? "orange"
                                                          : "zinc"
                                                }
                                            >
                                                {ticket.priority}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                </div>
            )}

            {/* ============================================================ */}
            {/* TIMELINE TAB                                                 */}
            {/* ============================================================ */}
            {activeTab === "timeline" && (
                <div className="mt-6">
                    <Card>
                        <h3 className="mb-6 text-sm font-semibold text-zinc-900 dark:text-white">Event Timeline</h3>
                        <div className="mb-6 flex gap-3">
                            {[
                                { type: "detection", label: "Detection", color: "bg-red-500" },
                                { type: "correlation", label: "Correlation", color: "bg-blue-500" },
                                { type: "action", label: "Action", color: "bg-green-500" },
                                { type: "escalation", label: "Escalation", color: "bg-orange-500" },
                            ].map((t) => (
                                <div key={t.type} className="flex items-center gap-1.5 text-xs text-zinc-500">
                                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${t.color}`} />
                                    {t.label}
                                </div>
                            ))}
                        </div>
                        <div className="relative space-y-0">
                            {/* Connecting line */}
                            <div className="absolute bottom-0 left-[18px] top-0 w-0.5 bg-gradient-to-b from-blue-400 via-blue-300 to-blue-200 dark:from-blue-600 dark:via-blue-700 dark:to-blue-800" />

                            {timelineEvents.map((te, i) => (
                                <div key={te.id} className="relative flex gap-4 py-3">
                                    {/* Node */}
                                    <div
                                        className={`relative z-10 mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 bg-white shadow-md dark:bg-zinc-900 ${timelineSevColor[te.severity]}`}
                                    >
                                        <div className={`h-3 w-3 rounded-full ${timelineTypeColor[te.type]}`} />
                                    </div>

                                    {/* Content */}
                                    <div className="min-w-0 flex-1 rounded-xl border border-zinc-200/50 bg-white/50 p-3 transition-all hover:shadow-md dark:border-zinc-700/40 dark:bg-zinc-800/50">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs font-semibold text-zinc-500">
                                                {te.time}
                                            </span>
                                            <Badge
                                                color={
                                                    te.system === "soc"
                                                        ? "blue"
                                                        : te.system === "ndr"
                                                          ? "cyan"
                                                          : te.system === "ztna"
                                                            ? "green"
                                                            : "zinc"
                                                }
                                            >
                                                {te.system.toUpperCase()}
                                            </Badge>
                                            <Badge
                                                color={
                                                    te.severity === "critical"
                                                        ? "red"
                                                        : te.severity === "high"
                                                          ? "orange"
                                                          : te.severity === "medium"
                                                            ? "yellow"
                                                            : "zinc"
                                                }
                                            >
                                                {te.type}
                                            </Badge>
                                        </div>
                                        <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-white">
                                            {te.title}
                                        </p>
                                        <p className="mt-0.5 text-xs text-zinc-500">{te.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}

            {/* ============================================================ */}
            {/* EVIDENCE TAB                                                 */}
            {/* ============================================================ */}
            {activeTab === "evidence" && (
                <div className="mt-6 space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                            Collected Evidence ({MOCK_EVIDENCE.length} items)
                        </h3>
                        <Button color="blue">
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Export All
                        </Button>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {MOCK_EVIDENCE.map((ev) => (
                            <Card key={ev.id}>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-2">
                                        <div
                                            className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${ev.type === "malware_sample" ? "from-red-500/20 to-red-600/20" : ev.type === "memory_dump" ? "from-purple-500/20 to-purple-600/20" : "from-blue-500/20 to-blue-600/20"}`}
                                        >
                                            <span className="text-[10px] font-bold text-zinc-600 dark:text-zinc-300">
                                                {evidenceTypeIcon[ev.type]}
                                            </span>
                                        </div>
                                        <Badge color={evidenceTypeColor[ev.type]}>{ev.type.replace("_", " ")}</Badge>
                                    </div>
                                    <Badge
                                        color={
                                            ev.status === "analyzed"
                                                ? "green"
                                                : ev.status === "analyzing"
                                                  ? "yellow"
                                                  : "zinc"
                                        }
                                    >
                                        {ev.status}
                                    </Badge>
                                </div>
                                <p
                                    className="mt-3 truncate font-mono text-xs font-medium text-zinc-900 dark:text-white"
                                    title={ev.name}
                                >
                                    {ev.name}
                                </p>
                                <dl className="mt-2 space-y-1 text-xs">
                                    <div className="flex justify-between">
                                        <dt className="text-zinc-500">Size</dt>
                                        <dd className="text-zinc-700 dark:text-zinc-300">{ev.size}</dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-zinc-500">Collected</dt>
                                        <dd className="font-mono text-zinc-700 dark:text-zinc-300">{ev.collectedAt}</dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-zinc-500">By</dt>
                                        <dd className="text-zinc-700 dark:text-zinc-300">{ev.collectedBy}</dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-zinc-500">Hash</dt>
                                        <dd className="truncate font-mono text-zinc-400" title={ev.hash}>
                                            {ev.hash}
                                        </dd>
                                    </div>
                                </dl>
                                <div className="mt-3 flex gap-2">
                                    <Button plain className="flex-1">
                                        <EyeIcon className="h-3.5 w-3.5" /> View
                                    </Button>
                                    <Button plain className="flex-1">
                                        <ArrowDownTrayIcon className="h-3.5 w-3.5" /> Download
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* ============================================================ */}
            {/* CONTAINMENT TAB                                              */}
            {/* ============================================================ */}
            {activeTab === "containment" && (
                <div className="mt-6 space-y-6">
                    {/* Containment progress */}
                    <div className="grid gap-4 sm:grid-cols-3">
                        <StatCard
                            label="Actions Completed"
                            value={`${containmentActions.filter((a) => a.status === "completed").length}/${containmentActions.length}`}
                        />
                        <StatCard
                            label="Critical Actions"
                            value={`${containmentActions.filter((a) => a.critical && a.status === "completed").length}/${containmentActions.filter((a) => a.critical).length}`}
                        />
                        <StatCard
                            label="Containment Status"
                            value={
                                containmentActions.every((a) => a.status === "completed") ? "Complete" : "In Progress"
                            }
                        />
                    </div>

                    {/* Containment actions checklist */}
                    <Card>
                        <h3 className="mb-4 text-sm font-semibold text-zinc-900 dark:text-white">
                            Containment Actions
                        </h3>
                        <div className="space-y-3">
                            {containmentActions.map((ca) => {
                                const Icon = ca.icon;
                                return (
                                    <div
                                        key={ca.id}
                                        className={`flex items-center gap-4 rounded-xl border p-4 transition-all ${ca.status === "completed" ? "border-green-200/60 bg-green-50/30 dark:border-green-800/30 dark:bg-green-950/10" : "border-zinc-200/60 dark:border-zinc-700/40"}`}
                                    >
                                        <Switch
                                            checked={ca.status === "completed"}
                                            onChange={() => toggleContainment(ca.id)}
                                        />
                                        <div
                                            className={`flex h-9 w-9 items-center justify-center rounded-lg ${ca.status === "completed" ? "bg-green-100 dark:bg-green-900/30" : "bg-zinc-100 dark:bg-zinc-800"}`}
                                        >
                                            <Icon className={`h-5 w-5 ${containmentStatusColor[ca.status]}`} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={`text-sm font-medium ${ca.status === "completed" ? "text-zinc-400 line-through" : "text-zinc-900 dark:text-white"}`}
                                                >
                                                    {ca.action}
                                                </span>
                                                {ca.critical && <Badge color="red">Critical</Badge>}
                                                <Badge
                                                    color={
                                                        ca.status === "completed"
                                                            ? "green"
                                                            : ca.status === "in_progress"
                                                              ? "yellow"
                                                              : "zinc"
                                                    }
                                                >
                                                    {ca.status.replace("_", " ")}
                                                </Badge>
                                            </div>
                                            <p className="mt-0.5 text-xs text-zinc-500">{ca.description}</p>
                                            <div className="mt-1 flex items-center gap-3 text-[10px] text-zinc-400">
                                                <span>Assignee: {ca.assignee}</span>
                                                {ca.completedAt && <span>Completed: {ca.completedAt}</span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>

                    {/* Response Playbook */}
                    <Card>
                        <h3 className="mb-4 text-sm font-semibold text-zinc-900 dark:text-white">
                            Response Playbook - Incident Response
                        </h3>
                        <div className="space-y-2">
                            {playbook.map((step, stepIdx) => {
                                const completedSubsteps = step.substeps.filter((ss) => ss.completed).length;
                                const totalSubsteps = step.substeps.length;
                                const progress = Math.round((completedSubsteps / totalSubsteps) * 100);
                                const isExpanded = expandedPlaybookStep === step.id;

                                return (
                                    <div
                                        key={step.id}
                                        className="rounded-xl border border-zinc-200/60 dark:border-zinc-700/40"
                                    >
                                        <button
                                            className="flex w-full items-center gap-3 p-4 text-left"
                                            onClick={() => setExpandedPlaybookStep(isExpanded ? null : step.id)}
                                        >
                                            <div
                                                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${step.completed ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400" : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800"}`}
                                            >
                                                {step.completed ? <CheckCircleIcon className="h-5 w-5" /> : stepIdx + 1}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <Badge color="zinc">{step.phase}</Badge>
                                                    <span className="text-sm font-medium text-zinc-900 dark:text-white">
                                                        {step.title}
                                                    </span>
                                                </div>
                                                <div className="mt-1 flex items-center gap-2">
                                                    <div className="h-1.5 w-24 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-300 ${step.completed ? "bg-green-500" : "bg-blue-500"}`}
                                                            style={{ width: `${progress}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-[10px] text-zinc-400">
                                                        {completedSubsteps}/{totalSubsteps}
                                                    </span>
                                                </div>
                                            </div>
                                            {isExpanded ? (
                                                <ChevronDownIcon className="h-4 w-4 text-zinc-400" />
                                            ) : (
                                                <ChevronRightIcon className="h-4 w-4 text-zinc-400" />
                                            )}
                                        </button>
                                        {isExpanded && (
                                            <div className="border-t border-zinc-200/60 px-4 py-3 dark:border-zinc-700/40">
                                                <p className="mb-3 text-xs text-zinc-500">{step.description}</p>
                                                <div className="space-y-2">
                                                    {step.substeps.map((ss, ssIdx) => (
                                                        <label
                                                            key={ssIdx}
                                                            className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800"
                                                        >
                                                            <Checkbox
                                                                checked={ss.completed}
                                                                onChange={() => togglePlaybookSubstep(step.id, ssIdx)}
                                                                color="blue"
                                                            />
                                                            <span
                                                                className={`text-sm ${ss.completed ? "text-zinc-400 line-through" : "text-zinc-700 dark:text-zinc-300"}`}
                                                            >
                                                                {ss.text}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                </div>
            )}

            {/* Create ticket button */}
            <div className="mt-6">
                <Button color="blue" onClick={() => onNavigateDetail({ view: "tickets", detailId: undefined })}>
                    <TicketIcon className="h-4 w-4" />
                    {t("socDetail.createTicket")}
                </Button>
            </div>
        </>
    );
}
